import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Grid,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DirectionsCarOutlinedIcon from "@material-ui/icons/DirectionsCarOutlined";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";
import CodeOutlinedIcon from "@material-ui/icons/CodeOutlined";
import { useDispatch } from "react-redux";

import { OAuth as OAuthActions } from "../../js/actions";
import { Context } from "../../js/utils/Context";
import AddVehicleDialog from "../AddVehicleNew";

const ACCENT = "#1d4ed8";
const ACCENT_DARK = "#1e40af";
const ACCENT_SOFT_BG = "#eff6ff";
const BORDER = "#e5e7eb";
const TEXT_TITLE = "#0f172a";
const TEXT_LABEL = "#94a3b8";
const TEXT_VALUE = "#1f2937";
const TEXT_MUTED = "#64748b";
const SURFACE = "#ffffff";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    padding: theme.spacing(1.5, 1.5),
    backgroundColor: SURFACE,
    fontFamily: theme.typography.fontFamily,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${BORDER}`,
  },
  headerLeft: {
    minWidth: 0,
  },
  headerTitle: {
    fontWeight: 700,
    color: TEXT_TITLE,
    fontSize: "1.1rem !important",
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
  },
  headerSubtitle: {
    color: TEXT_MUTED,
    fontSize: "0.78rem !important",
    fontWeight: 400,
    marginTop: 2,
  },
  addBtn: {
    color: "white",
    backgroundColor: ACCENT,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.85rem !important",
    padding: theme.spacing(0.75, 2),
    minWidth: 0,
    borderRadius: 8,
    boxShadow: "0 1px 2px rgba(37, 99, 235, 0.25)",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: ACCENT_DARK,
      boxShadow: "0 2px 6px rgba(37, 99, 235, 0.3)",
    },
  },

  card: {
    border: `1px solid ${BORDER}`,
    borderRadius: 14,
    overflow: "hidden",
    backgroundColor: SURFACE,
    cursor: "pointer",
    transition:
      "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    "&:hover": {
      borderColor: "#cbd5e1",
      boxShadow: "0 2px 10px rgba(15, 23, 42, 0.05)",
    },
    "&:focus-visible": {
      outline: "none",
      borderColor: ACCENT,
      boxShadow: `0 0 0 3px rgba(37, 99, 235, 0.2)`,
    },
  },
  cardSelected: {
    borderColor: ACCENT,
    boxShadow: "none",
    "&:hover": {
      borderColor: ACCENT,
      boxShadow: "none",
    },
  },

  cardHeader: {
    padding: theme.spacing(1.5, 1.75),
    backgroundColor: "#f3f4f6",
    color: TEXT_TITLE,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    transition: "background 180ms ease, color 180ms ease",
  },
  cardHeaderSelected: {
    background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
    color: "white",
  },
  iconBubble: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: SURFACE,
    color: ACCENT,
    flexShrink: 0,
    border: `1px solid ${BORDER}`,
  },
  iconStyle: {
    fontSize: "22px !important",
  },
  iconBubbleSelected: {
    backgroundColor: "rgba(255,255,255,0.2)",
    color: "white",
    border: "1px solid transparent",
  },
  titleBox: {
    minWidth: 0,
    flex: 1,
  },
  cardTitle: {
    fontWeight: "800 !important",
    fontSize: "1.0rem !important",
    lineHeight: "1.1 !important",
    color: TEXT_TITLE,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    margin: 0,
    padding: 0,
  },
  cardTitleSelected: {
    color: "white",
  },
  cardBody: {
    padding: theme.spacing(2),
    backgroundColor: SURFACE,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
  },
  fieldBlock: {
    minWidth: 0,
  },
  fieldLabelRow: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    color: TEXT_LABEL,
    fontSize: "0.72rem !important",
    fontWeight: 500,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  fieldLabelIcon: {
    fontSize: "14px !important",
    color: TEXT_LABEL,
  },
  fieldValue: {
    color: TEXT_TITLE,
    fontSize: "0.8rem !important",
    fontWeight: 600,
    lineHeight: 1.2,
    wordBreak: "break-word",
  },
  emptyState: {
    padding: theme.spacing(5),
    textAlign: "center",
    color: TEXT_MUTED,
    border: `1px dashed ${BORDER}`,
    borderRadius: 12,
  },
  loadingBox: {
    padding: theme.spacing(5),
    display: "flex",
    justifyContent: "center",
  },
}));

const Field = ({ icon: Icon, label, value, classes }) => (
  <Box className={classes.fieldBlock}>
    <Box className={classes.fieldLabelRow}>
      {Icon ? <Icon className={classes.fieldLabelIcon} /> : null}
      <span>{label}</span>
    </Box>
    <Typography className={classes.fieldValue}>
      {value !== undefined && value !== null && value !== ""
        ? String(value)
        : "—"}
    </Typography>
  </Box>
);

const VehicleCard = ({ vehicle, selected, onSelect, classes }) => {
  return (
    <Box
      className={`${classes.card} ${selected ? classes.cardSelected : ""}`}
      onClick={() => onSelect(vehicle.uid, vehicle)}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(vehicle.uid, vehicle);
        }
      }}
    >
      <Box
        className={`${classes.cardHeader} ${selected ? classes.cardHeaderSelected : ""
          }`}
      >
        <Box
          className={`${classes.iconBubble} ${selected ? classes.iconBubbleSelected : ""
            }`}
        >
          <DirectionsCarOutlinedIcon className={classes.iconStyle} />
        </Box>
        <Box className={classes.titleBox}>
          <Typography
            className={`${classes.cardTitle} ${selected ? classes.cardTitleSelected : ""
              }`}
          >
            {vehicle.modelName || "Unnamed Vehicle"}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.cardBody}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Field
              classes={classes}
              icon={CodeOutlinedIcon}
              label="Model Code"
              value={vehicle.modelCode}
            />
          </Grid>
          <Grid item xs={6}>
            <Field
              classes={classes}
              icon={EventOutlinedIcon}
              label="Year"
              value={vehicle.yearOfManufacturing}
            />
          </Grid>
          <Grid item xs={6}>
            <Field
              classes={classes}
              icon={LocalOfferOutlinedIcon}
              label="Variant"
              value={vehicle.variant}
            />
          </Grid>
          <Grid item xs={6}>
            <Field
              classes={classes}
              icon={RoomOutlinedIcon}
              label="Market"
              value={vehicle.market}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const VehicleDetails = ({ selectedUid: selectedUidProp, onSelectedChange }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [makeSelected] = useContext(Context);

  const [vehicles, setVehicles] = useState([]);
  const [internalSelectedUid, setInternalSelectedUid] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const isControlled = selectedUidProp !== undefined;
  const selectedUid = isControlled ? selectedUidProp : internalSelectedUid;
  const setSelectedUid = (uid, vehicle) => {
    if (isControlled) {
      if (onSelectedChange) onSelectedChange(uid, vehicle);
    } else {
      setInternalSelectedUid(uid);
    }
  };

  const loadVehicles = async () => {
    if (!makeSelected || !makeSelected.uid) {
      setVehicles([]);
      setSelectedUid("");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    try {
      const response = await dispatch(
        OAuthActions.getVehicleDetails(makeSelected.uid)
      );
      const list = response?._embedded?.vehicleModelDtoes || [];
      setVehicles(list);
      if (selectedUid) {
        const stillThere = list.find((v) => v.uid === selectedUid);
        if (stillThere) {
          setSelectedUid(selectedUid, stillThere);
        } else {
          setSelectedUid("");
        }
      }
    } catch (err) {
      setErrorMsg("Unable to load vehicles. Please try again.");
      setVehicles([]);
      setSelectedUid("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, makeSelected]);

  const renderBody = () => {
    if (!makeSelected || !makeSelected.uid) {
      return (
        <Box className={classes.emptyState}>
          <Typography variant="body2">
            Please select an OEM on the dashboard before viewing vehicles.
          </Typography>
        </Box>
      );
    }
    if (loading) {
      return (
        <Box className={classes.loadingBox}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (errorMsg) {
      return (
        <Box className={classes.emptyState}>
          <Typography color="error" variant="body2">
            {errorMsg}
          </Typography>
        </Box>
      );
    }
    if (vehicles.length === 0) {
      return (
        <Box className={classes.emptyState}>
          <Typography variant="body2">
            No vehicles available for the selected OEM.
          </Typography>
        </Box>
      );
    }
    return (
      <Grid container spacing={2}>
        {vehicles.map((v) => (
          <Grid item xs={12} sm={6} md={3} key={v.uid}>
            <VehicleCard
              vehicle={v}
              selected={selectedUid === v.uid}
              onSelect={setSelectedUid}
              classes={classes}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.headerRow}>
        <Box className={classes.headerLeft}>
          <Typography className={classes.headerTitle}>
            Select a Vehicle
          </Typography>
          <Typography className={classes.headerSubtitle}>
            Choose from available vehicles or add a new one
          </Typography>
        </Box>
        <Button
          className={classes.addBtn}
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          variant="contained"
          disableElevation
        >
          Add Vehicle
        </Button>
      </Box>

      {renderBody()}

      <AddVehicleDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdded={loadVehicles}
      />
    </Box>
  );
};

export default VehicleDetails;
